<template>
	<div>
		<Card>
			
			<Form ref="searchForm" :model="searchForm" inline :label-width="80">
				<FormItem :label="$t('SplitAccount.Station')">
					<Select v-model="searchForm.stationId" :placeholder="$t('SplitAccount.SelectStation')" style="width: 200px" clearable>
						<Option v-for="item in stationList" :key="item.StationId" :value="item.StationId">
							{{ item.StationName || '-' }}
						</Option>
					</Select>
				</FormItem>
				<FormItem :label="$t('SplitAccount.SplitType')">
					<Select v-model="searchForm.splitType" :placeholder="$t('SplitAccount.SelectSplitType')" style="width: 150px" clearable>
						<Option :value="1">{{ $t('SplitAccount.PlatformSplit') }}</Option>
						<Option :value="2">{{ $t('SplitAccount.StationSplit') }}</Option>
					</Select>
				</FormItem>
				<FormItem>
					<Button type="primary" @click="handleSearch">{{ $t('Common.Search') }}</Button>
					<Button type="default" @click="handleReset" style="margin-left: 8px;">{{ $t('Common.Reset') }}</Button>
				</FormItem>
			</Form>
		</Card>

		<Card>
			<Table border :columns="columns" :data="tableData" :loading="tableLoading">
				<template #splitType="{ row }">
					<Tag :color="row.SplitType === 1 ? 'primary' : 'success'">
						{{ row.SplitType === 1 ? $t('SplitAccount.PlatformSplit') : $t('SplitAccount.StationSplit') }}
					</Tag>
				</template>
				<template #balance="{ row }">
					<span style="color: #52c41a; font-weight: bold;">
						¥{{ formatMoney(row.Balance) }}
					</span>
				</template>
				<template #frozenAmount="{ row }">
					<span style="color: #ed4014; font-weight: bold;">
						¥{{ formatMoney(row.FrozenAmount) }}
					</span>
				</template>
				<template #totalAmount="{ row }">
					<span style="font-weight: bold;">
						¥{{ formatMoney(row.Balance + row.FrozenAmount) }}
					</span>
				</template>
				<template #updateTime="{ row }">
					{{ formatDateTime(row.UpdateTime) }}
				</template>
			</Table>
			<div style="margin-top: 16px; text-align: right;">
				<Page
					:total="pagination.total"
					:current="pagination.current"
					:page-size="pagination.pageSize"
					:page-size-opts="[10, 20, 50, 100]"
					show-total
					show-sizer
					@on-change="handlePageChange"
					@on-page-size-change="handlePageSizeChange"
				/>
			</div>
		</Card>

		<!-- 详情弹窗 -->
		<Modal
			v-model="detailModalVisible"
			:title="$t('SplitAccount.DetailTitle')"
			:mask-closable="false"
			width="600"
		>
			<div v-if="currentAccount">
				<Row :gutter="16">
					<Col span="12">
						<div class="detail-item">
							<span class="detail-label">{{ $t('SplitAccount.OperatorId') }}：</span>
							<span class="detail-value">{{ currentAccount.OperatorId || '-' }}</span>
						</div>
					</Col>
					<Col span="12">
						<div class="detail-item">
							<span class="detail-label">{{ $t('SplitAccount.StationId') }}：</span>
							<span class="detail-value">{{ currentAccount.StationId || '-' }}</span>
						</div>
					</Col>
				</Row>
				<Divider />
				<Row :gutter="16">
					<Col span="12">
						<div class="detail-item">
							<span class="detail-label">{{ $t('SplitAccount.SplitType') }}：</span>
							<Tag :color="currentAccount.SplitType === 1 ? 'primary' : 'success'">
								{{ currentAccount.SplitType === 1 ? $t('SplitAccount.PlatformSplit') : $t('SplitAccount.StationSplit') }}
							</Tag>
						</div>
					</Col>
					<Col span="12">
						<div class="detail-item">
							<span class="detail-label">{{ $t('SplitAccount.AvailableBalance') }}：</span>
							<span class="detail-value" style="color: #52c41a; font-weight: bold;">
								¥{{ formatMoney(currentAccount.Balance) }}
							</span>
						</div>
					</Col>
				</Row>
				<Row :gutter="16">
					<Col span="12">
						<div class="detail-item">
							<span class="detail-label">{{ $t('SplitAccount.FrozenAmount') }}：</span>
							<span class="detail-value" style="color: #ed4014; font-weight: bold;">
								¥{{ formatMoney(currentAccount.FrozenAmount) }}
							</span>
						</div>
					</Col>
					<Col span="12">
						<div class="detail-item">
							<span class="detail-label">{{ $t('SplitAccount.TotalAmount') }}：</span>
							<span class="detail-value" style="font-weight: bold;">
								¥{{ formatMoney(currentAccount.Balance + currentAccount.FrozenAmount) }}
							</span>
						</div>
					</Col>
				</Row>
				<Divider />
				<Row>
					<Col span="24">
						<div class="detail-item">
							<span class="detail-label">{{ $t('SplitAccount.UpdateTime') }}：</span>
							<span class="detail-value">{{ formatDateTime(currentAccount.UpdateTime) }}</span>
						</div>
					</Col>
				</Row>
			</div>
			<div slot="footer">
				<Button @click="detailModalVisible = false">{{ $t('SplitAccount.Close') }}</Button>
			</div>
		</Modal>

		<!-- 申请提现弹窗 -->
		<Modal
			v-model="withdrawModalVisible"
			:title="$t('SplitAccount.WithdrawTitle')"
			:mask-closable="false"
			width="600"
		>
			<Form ref="withdrawForm" :model="withdrawForm" :rules="withdrawRules" :label-width="120">
				<FormItem :label="$t('SplitAccount.Station')">
					<span>{{ (currentAccountForWithdraw && currentAccountForWithdraw.StationName) || '-' }}</span>
				</FormItem>
				<FormItem :label="$t('SplitAccount.SplitType')">
					<Tag :color="(currentAccountForWithdraw && currentAccountForWithdraw.SplitType === 1) ? 'primary' : 'success'">
						{{ (currentAccountForWithdraw && currentAccountForWithdraw.SplitType === 1) ? $t('SplitAccount.PlatformSplit') : $t('SplitAccount.StationSplit') }}
					</Tag>
				</FormItem>
				<FormItem :label="$t('SplitAccount.AvailableBalance')">
					<span style="color: #52c41a; font-weight: bold; font-size: 16px;">
						¥{{ formatMoney((currentAccountForWithdraw && currentAccountForWithdraw.Balance) || 0) }}
					</span>
				</FormItem>
				<FormItem :label="$t('SplitAccount.WithdrawAmount')" prop="amount">
					<Input
						v-model="withdrawForm.amountStr"
						:placeholder="$t('SplitAccount.PleaseEnterWithdrawAmount')"
						style="width: 100%;"
						@on-blur="handleAmountBlur"
					>
						<span slot="prepend">¥</span>
					</Input>
					<div style="margin-top: 5px; color: #999; font-size: 12px;">
						{{ $t('SplitAccount.AvailableBalance') }}：¥{{ formatMoney((currentAccountForWithdraw && currentAccountForWithdraw.Balance) || 0) }}
					</div>
				</FormItem>
				<FormItem :label="$t('SplitAccount.WithdrawAccount')" prop="accountId">
					<Select v-model="withdrawForm.accountId" :placeholder="$t('SplitAccount.PleaseSelectWithdrawAccount')" :loading="accountListLoading">
						<Option v-for="item in withdrawAccountList" :key="item.Id" :value="item.Id">
							{{ item.Channel === 'stripe' ? 'Stripe' : item.Channel }} - {{ item.ReceiveAccount }} ({{ item.AccountName }})
						</Option>
					</Select>
				</FormItem>
				<FormItem :label="$t('SplitAccount.Remark')" prop="remark">
					<Input v-model="withdrawForm.remark" type="textarea" :rows="3" :placeholder="$t('SplitAccount.PleaseEnterRemark')" />
				</FormItem>
			</Form>
			<div slot="footer">
				<Button @click="handleWithdrawCancel">{{ $t('SplitAccount.Cancel') }}</Button>
				<Button type="primary" @click="handleWithdrawOk" :loading="withdrawLoading">{{ $t('SplitAccount.Confirm') }}</Button>
			</div>
		</Modal>
	</div>
</template>

<script>
	import { mapState } from 'vuex';
	import webapi from '../../../webapi';

	export default {
		name: 'SplitAccount',
		data() {
			return {
				searchForm: {
					stationId: undefined,
					splitType: undefined
				},
				columns: [],
				tableData: [],
				tableLoading: false,
				pagination: {
					current: 1,
					pageSize: 10,
					total: 0
				},
				detailModalVisible: false,
				currentAccount: null,
				stationList: [],
				withdrawModalVisible: false,
				withdrawLoading: false,
				accountListLoading: false,
				currentAccountForWithdraw: null,
				withdrawAccountList: [],
				withdrawForm: {
					amount: null,
					amountStr: '',
					accountId: undefined,
					remark: ''
				},
				withdrawRules: {}
			};
		},
		computed: {
			...mapState({
				Stations: 'Stations'
			})
		},
		watch: {
			// 监听语言变化，重新初始化列定义和验证规则
			'$i18n.locale'() {
				this.initColumns();
				this.initWithdrawRules();
			}
		},
		created() {
			this.initColumns();
			this.initWithdrawRules();
			this.getStationList();
			this.getAccountList();
		},
		methods: {
			// 初始化验证规则
			initWithdrawRules() {
				this.withdrawRules = {
					amount: [
						{ required: true, type: 'number', message: this.$t('SplitAccount.PleaseEnterWithdrawAmount'), trigger: 'change' }
					],
					accountId: [
						{ required: true, type: 'number', message: this.$t('SplitAccount.PleaseSelectWithdrawAccount'), trigger: 'change' }
					]
				};
			},
			// 初始化表格列
			initColumns() {
				this.columns = [
					{
						title: this.$t('SplitAccount.Index'),
						type: 'index',
						width: 80,
						align: 'center'
					},
					{
						title: this.$t('SplitAccount.Operator'),
						key: 'OperatorName',
						minWidth: 150
					},
					{
						title: this.$t('SplitAccount.StationName'),
						key: 'StationName',
						minWidth: 200
					},
					{
						title: this.$t('SplitAccount.SplitType'),
						slot: 'splitType',
						width: 120,
						align: 'center'
					},
					{
						title: this.$t('SplitAccount.Balance'),
						slot: 'balance',
						width: 150,
						align: 'right'
					},
					{
						title: this.$t('SplitAccount.FrozenAmount'),
						slot: 'frozenAmount',
						width: 150,
						align: 'right'
					},
					{
						title: this.$t('SplitAccount.TotalAmount'),
						slot: 'totalAmount',
						width: 150,
						align: 'right'
					},
					{
						title: this.$t('SplitAccount.UpdateTime'),
						slot: 'updateTime',
						width: 180,
						align: 'center'
					},
					{
						title: this.$t('SplitAccount.Operation'),
						key: 'action',
						width: 150,
						align: 'center',
						fixed: 'right',
						render: (h, params) => {
							return h('div', [
								h('Button', {
									props: {
										type: 'primary',
										size: 'small',
										disabled: params.row.Balance <= 0 || params.row.SplitType === 1
									},
									style: {
										marginRight: '5px'
									},
									on: {
										click: () => {
											this.handleApplyWithdraw(params.row);
										}
									}
								}, this.$t('SplitAccount.ApplyWithdraw')),
								h('Button', {
									props: {
										type: 'default',
										size: 'small'
									},
									on: {
										click: () => {
											this.handleViewDetail(params.row);
										}
									}
								}, this.$t('SplitAccount.ViewDetail'))
							]);
						}
					}
				];
			},

			// 获取场站列表
			async getStationList() {
				try {
					const res = await this.$axios.post(webapi.base_api_url + '/Home/GetCurrentUserStations', {});
					if (res.status === 200 && res.data.Code === 0) {
						this.stationList = res.data.Data.List || [];
						console.log('场站列表数据:', this.stationList);
					}
				} catch (error) {
					console.error('获取场站列表失败:', error);
					this.$Message.error(this.$t('SplitAccount.GetAccountListFail'));
				}
			},

			// 获取分账账号列表
			async getAccountList() {
				this.tableLoading = true;
				try {
					const params = {
						stationId: this.searchForm.stationId,
						splitType: this.searchForm.splitType,
						pageIndex: this.pagination.current,
						pageSize: this.pagination.pageSize
					};

					const res = await this.$axios.post(webapi.base_api_url + '/Split/GetSplitOperatorAccounts', params);

					if (res.status === 200 && res.data.Code === 0) {
						this.tableData = res.data.Data.List || [];
						this.pagination.total = res.data.Total || 0;
					} else {
						this.$Message.error(res.data.Message || this.$t('SplitAccount.GetAccountListFail'));
					}
				} catch (error) {
					console.error('获取分账账号列表失败:', error);
					this.$Message.error(this.$t('SplitAccount.GetAccountListFail'));
				} finally {
					this.tableLoading = false;
				}
			},

			// 搜索
			handleSearch() {
				this.pagination.current = 1;
				this.getAccountList();
			},

			// 重置
			handleReset() {
				this.searchForm = {
					stationId: undefined,
					splitType: undefined
				};
				this.pagination.current = 1;
				this.getAccountList();
			},

			// 查看详情
			handleViewDetail(row) {
				this.currentAccount = { ...row };
				this.detailModalVisible = true;
			},

			// 申请提现
			async handleApplyWithdraw(row) {
				// 平台分账类型不能申请提现
				if (row.SplitType === 1) {
					this.$Message.warning(this.$t('SplitAccount.PlatformSplitNotSupport'));
					return;
				}

				// 余额不足不能申请提现
				if (row.Balance <= 0) {
					this.$Message.warning(this.$t('SplitAccount.InsufficientBalance'));
					return;
				}

				this.currentAccountForWithdraw = { ...row };
				this.withdrawModalVisible = true;

				// 重置表单
				this.withdrawForm = {
					amount: null,
					amountStr: '',
					accountId: undefined,
					remark: ''
				};

				// 加载提现账号列表
				await this.getWithdrawAccountList(row.StationId);
			},

			// 获取提现账号列表
			async getWithdrawAccountList(stationId) {
				this.accountListLoading = true;
				try {
					const params = {
						stationId: stationId,
						enable: 1
					};

					const res = await this.$axios.post(webapi.base_api_url + '/Split/GetWithdrawAccounts', params);

					if (res.status === 200 && res.data.Code === 0) {
						this.withdrawAccountList = res.data.Data.List || [];
					} else {
						this.$Message.error(res.data.Message || this.$t('SplitAccount.GetWithdrawAccountListFail'));
						this.withdrawAccountList = [];
					}
				} catch (error) {
					console.error('获取提现账号列表失败:', error);
					this.$Message.error(this.$t('SplitAccount.GetWithdrawAccountListFail'));
					this.withdrawAccountList = [];
				} finally {
					this.accountListLoading = false;
				}
			},

			// 确定申请提现
			handleWithdrawOk() {
				// 先验证金额
				if (!this.withdrawForm.amount || this.withdrawForm.amount <= 0) {
					this.$Message.error(this.$t('SplitAccount.InvalidAmount'));
					return;
				}

				this.$refs.withdrawForm.validate(async (valid) => {
					if (valid) {
						// 再次检查分账类型
						if (this.currentAccountForWithdraw.SplitType === 1) {
							this.$Message.error(this.$t('SplitAccount.PlatformSplitNotSupport'));
							this.withdrawModalVisible = false;
							return;
						}

						this.withdrawLoading = true;
						try {
							const params = {
								operatorId: this.currentAccountForWithdraw.OperatorId,
								stationId: this.currentAccountForWithdraw.StationId,
								splitType: this.currentAccountForWithdraw.SplitType,
								withdrawAmount: this.withdrawForm.amount,
								withdrawAccountId: this.withdrawForm.accountId,
								remark: this.withdrawForm.remark
							};

							const res = await this.$axios.post(webapi.base_api_url + '/Split/ApplyWithdraw', params);

							if (res.status === 200 && res.data.Code === 0) {
								this.$Message.success(this.$t('SplitAccount.ApplyWithdrawSuccess'));
								this.withdrawModalVisible = false;
								this.getAccountList();
							} else {
								this.$Message.error(res.data.Message || this.$t('SplitAccount.ApplyWithdrawFail'));
							}
						} catch (error) {
							console.error('申请提现失败:', error);
							this.$Message.error(this.$t('SplitAccount.ApplyWithdrawFail'));
						} finally {
							this.withdrawLoading = false;
						}
					}
				});
			},

			// 取消申请提现
			handleWithdrawCancel() {
				this.withdrawModalVisible = false;
				this.$refs.withdrawForm && this.$refs.withdrawForm.resetFields();
				this.currentAccountForWithdraw = null;
				this.withdrawAccountList = [];
			},

			// 分页变化
			handlePageChange(page) {
				this.pagination.current = page;
				this.getAccountList();
			},

			// 每页条数变化
			handlePageSizeChange(pageSize) {
				this.pagination.pageSize = pageSize;
				this.pagination.current = 1;
				this.getAccountList();
			},

			// 格式化金额
			formatMoney(amount) {
				if (amount === null || amount === undefined) return '0.00';
				return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
			},

			// 格式化日期时间
			formatDateTime(dateTime) {
				if (!dateTime) return '-';
				const date = new Date(dateTime);
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, '0');
				const day = String(date.getDate()).padStart(2, '0');
				const hours = String(date.getHours()).padStart(2, '0');
				const minutes = String(date.getMinutes()).padStart(2, '0');
				const seconds = String(date.getSeconds()).padStart(2, '0');
				return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
			},

			// 处理提现金额失去焦点
			handleAmountBlur() {
				const maxBalance = (this.currentAccountForWithdraw && this.currentAccountForWithdraw.Balance) || 0;

				// 将字符串转换为数字
				let amount = parseFloat(this.withdrawForm.amountStr);

				// 验证输入
				if (isNaN(amount) || amount <= 0) {
					this.withdrawForm.amountStr = '';
					this.withdrawForm.amount = null;
					return;
				}

				// 限制最大金额
				if (amount > maxBalance) {
					const msg = this.$i18n.locale === 'zh-CN' 
						? `提现金额不能超过可用余额 ${maxBalance.toFixed(2)} 元`
						: `Withdraw amount cannot exceed available balance ¥${maxBalance.toFixed(2)}`;
					this.$Message.warning(msg);
					amount = maxBalance;
				}

				// 保留两位小数
				this.withdrawForm.amount = parseFloat(amount.toFixed(2));
				this.withdrawForm.amountStr = this.withdrawForm.amount.toFixed(2);
			}
		}
	};
</script>

<style scoped>
	.detail-item {
		margin-bottom: 12px;
		line-height: 24px;
	}
	.detail-label {
		font-weight: bold;
		color: #666;
		margin-right: 8px;
	}
	.detail-value {
		color: #333;
	}
	:deep(.ivu-form-item) {
		margin-bottom: 16px;
	}
</style>
